import type { CSSProperties } from 'react';
import type { IPageControl } from './PageControl.interface';
import styles from './PageControl.module.css';

const ARROW_WIDTH_PC = 30;

/* (100pc - 2x ARROW_WIDTH_PC) / MAX(number_of_pages,1) 
    use up all remaining space for dots making sure to prevent divide by 0 errors */
const DEFAULT_BUTTON_SIZE = (props: IPageControl) =>
    `${(100 - ARROW_WIDTH_PC - ARROW_WIDTH_PC) / Math.max(props.totalPages, 1)}%`;

export enum StyleElem {
    Container = 'Container',

    ButtonArrowLeft = 'ButtonArrowLeft',
    ButtonArrowRight = 'ButtonArrowRight',
    ButtonDot = 'ButtonDot',

    IconArrowLeft = 'IconArrowLeft',
    IconArrowRight = 'IconArrowRight',
    IconDot = 'IconDot',
}

const DEFAULTS: Record<StyleElem, CSSProperties> = {
    [StyleElem.Container]: {},

    [StyleElem.ButtonArrowLeft]: {},
    [StyleElem.ButtonArrowRight]: {},
    [StyleElem.ButtonDot]: {
        marginLeft: '1.2vmin',
        marginRight: '1.2vmin',
    },

    [StyleElem.IconArrowLeft]: {},
    [StyleElem.IconArrowRight]: {},
    [StyleElem.IconDot]: {},
};

const PROPS_APPLICATOR: Record<StyleElem, (props: IPageControl) => CSSProperties> = {
    [StyleElem.Container]: (props: IPageControl) => ({
        backgroundColor: props.backgroundColor ?? 'transparent',
    }),

    [StyleElem.ButtonArrowLeft]: (props: IPageControl) => ({
        opacity: props.activeIndex > 0 ? 1 : 0.7,
        width: `${ARROW_WIDTH_PC}%`,
        display: 'inline-flex',
    }),
    [StyleElem.ButtonArrowRight]: (props: IPageControl) => ({
        opacity: props.activeIndex < props.totalPages - 1 ? 1 : 0.7,
        width: `${ARROW_WIDTH_PC}%`,
        display: 'inline-flex',
    }),
    [StyleElem.ButtonDot]: (props: IPageControl) => ({
        width: props.dotSize ?? DEFAULT_BUTTON_SIZE(props),
        marginLeft: props.dotSpacing ?? DEFAULTS.ButtonDot.marginLeft,
        marginRight: props.dotSpacing ?? DEFAULTS.ButtonDot.marginRight,
        display: 'inline-flex',
    }),

    [StyleElem.IconArrowLeft]: (props: IPageControl) => ({
        color: props.tintColor ?? '#fff',
    }),
    [StyleElem.IconArrowRight]: (props: IPageControl) => ({
        color: props.tintColor ?? '#fff',
    }),
    [StyleElem.IconDot]: (props: IPageControl) => ({
        color: props.tintColor ?? '#fff',
    }),
};

export function classNameForElement(element: StyleElem, props: IPageControl): string {
    const name = element.toString();
    const classLoad = styles[name];
    return classLoad || '';
}

export function styleForElement(
    elem: StyleElem,
    props: IPageControl,
    overrides: CSSProperties,
): CSSProperties {
    const style = { ...DEFAULTS[elem], ...PROPS_APPLICATOR[elem](props), ...overrides };

    return style;
}
